import { useState, useRef, useEffect } from "react";
import { Send, X, Loader2, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { ChatMessage } from "./ChatMessage";
import type { ChatMessage as ChatMessageType } from "../../lib/chat-service";

interface ChatWindowProps {
  messages: ChatMessageType[];
  isLoading: boolean;
  onSendMessage: (message: string) => void;
  onClose: () => void;
  onClear: () => void;
  logoUrl?: string;
  companyName?: string;
}

export function ChatWindow({
  messages,
  isLoading,
  onSendMessage,
  onClose,
  onClear,
  logoUrl = "/claims-app-logo-mini.svg",
  companyName = "Claims App",
}: ChatWindowProps) {
  const [inputValue, setInputValue] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;
    onSendMessage(inputValue);
    setInputValue("");
  };

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      transition={{ duration: 0.2 }}
      className="chat-window"
    >
      {/* Header */}
      <div className="chat-window-header">
        <div className="chat-window-header-left">
          <div className="chat-window-header-avatar">
            <img
              src={logoUrl}
              alt="Claims Bot"
              className="chat-window-header-avatar-img"
            />
          </div>
          <div>
            <h3 className="chat-window-title">Claims Bot</h3>
            <p className="chat-window-status">
              <span className="chat-window-status-dot" />
              Online
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <button
            onClick={onClear}
            className="chat-window-close"
            aria-label="Clear chat history"
            title="Clear conversation"
          >
            <Trash2 size={17} />
          </button>
          <button
            onClick={onClose}
            className="chat-window-close"
            aria-label="Close chat"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="chat-window-messages">
        <div className="chat-window-messages-inner">
          {messages.length === 0 && (
            <div className="chat-window-empty">
              <div className="chat-window-empty-icon">
                <img
                  src={logoUrl}
                  alt="Claims Bot"
                  className="chat-window-empty-img"
                />
              </div>
              <p className="chat-window-empty-text">
                Hi! I'm the Claims Bot. Ask me about projects, variations, claims, or anything related to {companyName}.
              </p>
            </div>
          )}

          {messages.map((msg) => (
            <ChatMessage key={msg.id} role={msg.role} content={msg.content} logoUrl={logoUrl} />
          ))}

          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="chat-message chat-message--bot"
            >
              <div className="chat-message-inner chat-message-inner--bot">
                <div className="chat-avatar-wrap">
                  <div className="chat-avatar chat-avatar--bot">
                    <img
                      src={logoUrl}
                      alt="Claims Bot"
                      className="chat-avatar-img"
                    />
                  </div>
                </div>
                <div className="chat-bubble chat-bubble--bot chat-bubble--typing">
                  <span className="chat-typing-text">Thinking...</span>
                  <Loader2 className="chat-typing-spinner spinning" size={14} />
                </div>
              </div>
            </motion.div>
          )}
          <div ref={scrollRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="chat-window-input-area">
        <form onSubmit={handleSubmit} className="chat-window-form">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type a message..."
            className="chat-window-input"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className="chat-window-send btn btn-primary"
            aria-label="Send message"
          >
            {isLoading ? (
              <Loader2 size={16} className="spinning" />
            ) : (
              <Send size={16} />
            )}
          </button>
        </form>
        <div className="chat-window-footer">
          <p className="chat-window-powered">Powered by {companyName}</p>
        </div>
      </div>
    </motion.div>
  );
}
